import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutGrid, Star, Inbox, LogOut, ExternalLink, Wrench, Settings, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { resolveImg } from "@/lib/api";
import { ProjectsManager } from "@/components/admin/ProjectsManager";
import { ReviewsManager } from "@/components/admin/ReviewsManager";
import { SubmissionsManager } from "@/components/admin/SubmissionsManager";
import { ServicesManager } from "@/components/admin/ServicesManager";
import { SettingsManager } from "@/components/admin/SettingsManager";
import { ChangePasswordModal } from "@/components/admin/ChangePasswordModal";

const TABS = [
  { id: "submissions", label: "Aanvragen", icon: Inbox },
  { id: "projects", label: "Projecten", icon: LayoutGrid },
  { id: "services", label: "Diensten", icon: Wrench },
  { id: "reviews", label: "Reviews", icon: Star },
  { id: "settings", label: "Instellingen", icon: Settings },
];

export default function Admin() {
  const { logout, user } = useAuth();
  const settings = useSettings();
  const navigate = useNavigate();
  const [tab, setTab] = useState("submissions");
  const [showPassword, setShowPassword] = useState(false);

  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-page">
      <header className="border-b border-white/10 bg-[#0a0a0a] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={resolveImg(settings.logo_url)} alt="Pruijn" className="h-10 w-auto object-contain" />
            <span className="text-white/40 text-sm hidden sm:block">Beheeromgeving</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noopener noreferrer" data-testid="view-site-link" className="text-white/60 hover:text-white text-sm flex items-center gap-2 border border-white/15 px-4 py-2"><ExternalLink size={15} /> <span className="hidden sm:inline">Website</span></a>
            <button onClick={() => setShowPassword(true)} data-testid="change-password-button" className="text-white/60 hover:text-white text-sm flex items-center gap-2 border border-white/15 px-4 py-2"><KeyRound size={15} /> <span className="hidden sm:inline">Wachtwoord</span></button>
            <button onClick={doLogout} data-testid="logout-button" className="text-white/60 hover:text-[#FF1A1A] text-sm flex items-center gap-2 border border-white/15 px-4 py-2"><LogOut size={15} /> <span className="hidden sm:inline">Uitloggen</span></button>
          </div>
        </div>
      </header>

      {showPassword && <ChangePasswordModal onClose={() => setShowPassword(false)} />}

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-2 mb-8 border-b border-white/10 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors border-b-2 -mb-[1px] ${
                  tab === t.id ? "text-white border-[#FF1A1A]" : "text-white/50 border-transparent hover:text-white/80"
                }`}
              >
                <Icon size={17} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "submissions" && <SubmissionsManager />}
        {tab === "projects" && <ProjectsManager />}
        {tab === "services" && <ServicesManager />}
        {tab === "reviews" && <ReviewsManager />}
        {tab === "settings" && <SettingsManager />}
      </div>
    </div>
  );
}
